import React, { useState, useEffect } from "react"
import MetaTags from "react-meta-tags"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  ModalFooter,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import Datatables from "components/Datatables/datatable"
import Select from "react-select"
import { get, post } from "helpers/api_helper"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const AdvanceSalary = () => {
  const [modal, setModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [records, setRecords] = useState({ rows: [], total_rows: 0 })
  const [employeeOptions, setEmployeeOptions] = useState([])
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    search: "",
  })

  // Form state
  const [formData, setFormData] = useState({
    employee_id: "",
    amount: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    reason: "",
    status: "Approved",
  })

  // Limit check result
  const [limitCheck, setLimitCheck] = useState({
    checked: false,
    isValid: true,
    maxAllowed: 0,
    grossSalary: 0,
    limitPercent: 0,
    message: "",
  })

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ]

  const years = Array.from({ length: 5 }, (_, i) => ({
    value: new Date().getFullYear() - i,
    label: (new Date().getFullYear() - i).toString(),
  }))

  const brownBtnStyle = {
    backgroundColor: "#3d1e08",
    borderColor: "#3d1e08",
    color: "#fff",
  }

  const toggleModal = () => {
    setModal(!modal)
    if (!modal) {
      setFormData({
        employee_id: "",
        amount: "",
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        reason: "",
        status: "Approved",
      })
      setLimitCheck({
        checked: false,
        isValid: true,
        maxAllowed: 0,
        grossSalary: 0,
        limitPercent: 0,
        message: "",
      })
    }
  }

  const getData = async () => {
    setIsLoading(true)
    const res = await get(
      `api/hrms/advance_salary?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.search}`
    )
    if (res.status === 200) {
      setRecords(res.response)
    }
    setIsLoading(false)
  }

  const getEmployees = async () => {
    const empRes = await post("api/selectors/getEmployees", { branch: "all" })
    if (empRes.status === 200) {
      setEmployeeOptions(
        empRes.response.map(emp => ({ value: emp.value, label: emp.label }))
      )
    }
  }

  useEffect(() => {
    getEmployees()
  }, [])

  useEffect(() => {
    getData()
  }, [pagination])

  // Perform limit check when employee_id or amount changes
  useEffect(() => {
    const checkLimit = async () => {
      if (!formData.employee_id || !formData.amount || parseFloat(formData.amount) <= 0) {
        setLimitCheck({
          checked: false,
          isValid: true,
          maxAllowed: 0,
          grossSalary: 0,
          limitPercent: 0,
          message: "",
        })
        return
      }

      const res = await get(
        `api/hrms/advance_salary/check_limit?employee_id=${formData.employee_id}&amount=${formData.amount}`
      )
      if (res.status && res.response) {
        const { max_allowed, gross_salary, limit_percent, is_valid } = res.response
        setLimitCheck({
          checked: true,
          isValid: is_valid,
          maxAllowed: max_allowed,
          grossSalary: gross_salary,
          limitPercent: limit_percent,
          message: is_valid 
            ? `Allowed limit: ₹${max_allowed.toFixed(2)} (${limit_percent}% of gross ₹${gross_salary.toFixed(2)})`
            : `Requested amount exceeds limit of ₹${max_allowed.toFixed(2)} (${limit_percent}% of gross ₹${gross_salary.toFixed(2)})!`,
        })
      }
    }

    const timer = setTimeout(() => {
      checkLimit()
    }, 400) // Debounce limit check requests

    return () => clearTimeout(timer)
  }, [formData.employee_id, formData.amount])

  const handleSubmit = async e => {
    e.preventDefault()
    if (!limitCheck.isValid) {
      toastr.error("Cannot submit: requested amount exceeds the advance limit")
      return
    }

    setIsLoading(true)
    const res = await post("api/hrms/advance_salary", formData)
    if (res.status === 200 || res.status === true) {
      toastr.success("Advance salary issued successfully")
      toggleModal()
      getData()
    } else {
      toastr.error(res.message || "Failed to issue advance salary")
    }
    setIsLoading(false)
  }

  const handleStatusUpdate = async (id, status) => {
    const res = await post("api/hrms/advance_salary/status", { id, status })
    if (res.status === 200 || res.status === true) {
      toastr.success(`Status updated to ${status}`)
      getData()
    } else {
      toastr.error(res.message || "Failed to update status")
    }
  }

  const handleDelete = async id => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      const res = await get(`api/hrms/advance_salary/delete?id=${id}`)
      if (res.status === 200 || res.status === true) {
        toastr.success("Record deleted successfully")
        getData()
      } else {
        toastr.error(res.message || "Failed to delete record")
      }
    }
  }

  const columns = [
    {
      dataField: "id",
      text: "#",
      formatter: (cell, row, index) =>
        (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      dataField: "employee_id",
      text: "EMPLOYEE",
      formatter: (cell, row) => (
        <div>
          <div style={{ fontWeight: "bold", color: "#3d1e08" }}>
            {row.employee_name}
          </div>
          <div style={{ fontSize: "11px", color: "#777" }}>
            ID: {row.employee_id}
          </div>
        </div>
      ),
    },
    {
      dataField: "amount",
      text: "AMOUNT",
      formatter: cell => <strong>₹{parseFloat(cell).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>,
    },
    {
      dataField: "month",
      text: "DEDUCTION PERIOD",
      formatter: (cell, row) => {
        const monthLabel = months.find(m => m.value == row.month)?.label || row.month
        return `${monthLabel} / ${row.year}`
      },
    },
    {
      dataField: "reason",
      text: "REASON",
      formatter: cell => cell || <span className="text-muted">No reason provided</span>,
    },
    {
      dataField: "status",
      text: "STATUS",
      formatter: (cell, row) => {
        let badgeClass = "bg-warning"
        if (cell === "Approved") badgeClass = "bg-success"
        if (cell === "Rejected") badgeClass = "bg-danger"

        return (
          <span className={`badge ${badgeClass}`} style={{ padding: "5px 10px" }}>
            {cell}
          </span>
        )
      },
    },
    {
      dataField: "created_at",
      text: "ISSUED DATE",
      formatter: cell => new Date(cell).toLocaleDateString('en-IN'),
    },
    {
      dataField: "action",
      text: "ACTIONS",
      formatter: (cell, row) => (
        <div className="d-flex">
          {row.status === "Pending" && (
            <>
              <Button
                color="success"
                size="sm"
                className="me-2"
                onClick={() => handleStatusUpdate(row.id, "Approved")}
                title="Approve Request"
              >
                <i className="fas fa-check"></i>
              </Button>
              <Button
                color="danger"
                size="sm"
                className="me-2"
                onClick={() => handleStatusUpdate(row.id, "Rejected")}
                title="Reject Request"
              >
                <i className="fas fa-times"></i>
              </Button>
            </>
          )}
          <Button
            color="light"
            size="sm"
            onClick={() => handleDelete(row.id)}
            title="Delete Record"
          >
            <i className="fas fa-trash-alt text-danger"></i>
          </Button>
        </div>
      ),
    },
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Dashboard | Advance Salary</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="HRMS" breadcrumbItem="Advance Salary" />

          <Card>
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                <h5 className="mb-0" style={{ fontWeight: "600", color: "#495057" }}>
                  Advance Salary Transactions
                </h5>
                <Button style={brownBtnStyle} onClick={toggleModal}>
                  <i className="fas fa-plus me-1"></i> Issue Advance Salary
                </Button>
              </div>

              <Datatables
                columns={columns}
                rows={records.rows}
                keyField="id"
                rowsLength={records.total_rows}
                isTableHead={false}
                isAdd={false}
                isSearch={true}
                loading={isLoading}
                ssr={setPagination}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Request Advance Modal */}
      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Issue Advance Salary</ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup className="mb-3">
              <Label>Select Employee <span className="text-danger">*</span></Label>
              <Select
                options={employeeOptions}
                placeholder="Search Employee..."
                onChange={e =>
                  setFormData(prev => ({ ...prev, employee_id: e.value }))
                }
                required
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <Label>Advance Amount (₹) <span className="text-danger">*</span></Label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={e =>
                  setFormData(prev => ({ ...prev, amount: e.target.value }))
                }
                required
                min="1"
              />
              {limitCheck.checked && (
                <div 
                  className={`mt-2 font-size-12 ${limitCheck.isValid ? 'text-success' : 'text-danger'}`}
                  style={{ fontWeight: "500" }}
                >
                  <i className={`fas ${limitCheck.isValid ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-1`}></i>
                  {limitCheck.message}
                </div>
              )}
            </FormGroup>

            <Row className="mb-3">
              <Col md={6}>
                <FormGroup>
                  <Label>Deduction Month <span className="text-danger">*</span></Label>
                  <Select
                    options={months}
                    defaultValue={months.find(m => m.value === formData.month)}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, month: e.value }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Deduction Year <span className="text-danger">*</span></Label>
                  <Select
                    options={years}
                    defaultValue={years.find(y => y.value === formData.year)}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, year: e.value }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-3">
              <Label>Status <span className="text-danger">*</span></Label>
              <Select
                options={[
                  { value: "Approved", label: "Approved (Active Deduction)" },
                  { value: "Pending", label: "Pending (Needs Approval)" },
                ]}
                defaultValue={{ value: "Approved", label: "Approved (Active Deduction)" }}
                onChange={e =>
                  setFormData(prev => ({ ...prev, status: e.value }))
                }
                required
              />
            </FormGroup>

            <FormGroup className="mb-0">
              <Label>Reason</Label>
              <Input
                type="textarea"
                rows="3"
                placeholder="Provide reason or description..."
                value={formData.reason}
                onChange={e =>
                  setFormData(prev => ({ ...prev, reason: e.target.value }))
                }
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
            <Button 
              style={brownBtnStyle} 
              type="submit" 
              disabled={isLoading || (limitCheck.checked && !limitCheck.isValid)}
            >
              Issue Advance
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  )
}

export default AdvanceSalary
